import { Injectable } from '@angular/core';
import { AccessoryResponse } from '@wb/core/interfaces/accessory';
import { FiPackage } from '@wb/core/interfaces/fi-package';
import { ProductResponse } from '@wb/core/interfaces/product';
import { gql, Query } from 'apollo-angular';

export interface AccessoriesResponse {
  accessories: {
    code: string;
    success: boolean;
    message: string;
    accessories: AccessoryResponse[];
    count: number;
  };
}

export interface AccessoriesFilter {
  accountUid?: string;
}

export interface AccessoriesQueryVariables {
  limit?: number;
  offset?: number;
  where?: AccessoriesFilter;
  readonly?: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class AccessoriesGQL extends Query<AccessoriesResponse, AccessoriesQueryVariables> {
  document = gql(`
    query accessories($where: ModelAccessoryFilterInput, $limit: Int, $offset: Int, $readonly: Boolean) {
      accessories(where: $where, limit: $limit, offset: $offset, readonly: $readonly) {
        code
        success
        message
        count
        accessories {
          uid
          title
          categories
          subaru_code
          price
          cost
          percent
          description
          sequence
          image
          image_name
          video_name
          video
          type
          attachments {
            id
            accessory_id
            file_name
            file
            deleted_at
            created_at
            updated_at
          }
          includedInPrice
          taxable_category
          last_updated
          exclude_from_finance_app
          criteria_count
        }
      }
    }
  `);
}

export interface GetAccessoryResponse {
  accessory: {
    code: string;
    success: boolean;
    message: string;
    accessory: AccessoryResponse;
  };
}

export interface AccessoryQueryVariables {
  uid: string;
}

@Injectable({
  providedIn: 'root',
})
export class AccessoryGQL extends Query<GetAccessoryResponse, AccessoryQueryVariables> {
  document = gql(`
    query accessory($uid: ID!) {
      accessory(uid: $uid) {
        code
        success
        message
        accessory {
          uid
          title
          categories
          subaru_code
          price
          cost
          percent
          description
          sequence
          image
          image_name
          video_name
          video
          type
          attachments {
            id
            accessory_id
            file_name
            file
            deleted_at
            created_at
            updated_at
          }
          includedInPrice
          taxable_category
          last_updated
          exclude_from_finance_app
          criteria_count
        }
      }
    }
  `);
}

export interface ProductsResponse {
  products: {
    code: string;
    success: boolean;
    message: string;
    products: ProductResponse[];
    count: number;
  };
}

export interface ProductsFilter {
  accountUid: string;
}

export interface ProductsQueryVariables {
  where: ProductsFilter;
  limit?: number;
  offset?: number;
  searchInput?: string | null;
  sortDirection?: string;
  sortColumn?: string;
}

@Injectable({
  providedIn: 'root',
})
export class ProductsGQL extends Query<ProductsResponse, ProductsQueryVariables> {
  document = gql(`
    query products(
      $where: ModelProductFilterInput,
      $limit: Int,
      $offset: Int,
      $searchInput: String,
      $sortDirection: String,
      $sortColumn: String
    ) {
      products(
        where: $where,
        limit: $limit,
        offset: $offset,
        searchInput: $searchInput,
        sortDirection: $sortDirection,
        sortColumn: $sortColumn
      ) {
        code
        success
        message
        count
        products {
          uid
          title
          price
          cost
          percent
          description
          image
          image_name
          video_name
          video
          type
          classification
          taxable_category
          sequence
          last_updated
          criteria_count
        }
      }
    }
  `);
}

export interface FiPackagesResponse {
  fiPackages: {
    code: string;
    success: boolean;
    message: string;
    fiPackages: FiPackage[];
  };
}

export interface PackagesFilter {
  accountUid: string;
}

export interface PackagesQueryVariables {
  limit?: number;
  offset?: number;
  where: PackagesFilter;
}

@Injectable({
  providedIn: 'root',
})
export class FiPackagesGQL extends Query<FiPackagesResponse, PackagesQueryVariables> {
  document = gql(`
    query fiPackages($where: ModelFiPackageFilterInput, $limit: Int, $offset: Int) {
      fiPackages(where: $where, limit: $limit, offset: $offset) {
        code
        success
        message
        fiPackages{
          uid
          name
          price
        }
      }
    }
  `);
}

@Injectable({
  providedIn: 'root',
})
export class FiPackageGQL extends Query<FiPackagesResponse> {
  document = gql(`
    query fiPackage($uid: ID!) {
      fiPackage(uid: $uid) {
        code
        success
        message
        fiPackage{
          uid
          name
          price
          image
          description
          products{
            id
            uid
            title
            cost
            price_override
            price
            taxable_category
            type
            classification
          }
          productOptions{
            uid
            title
            cost
            price_override
          }
          attachments{
            id
            file_name
            file
          }
        }
      }
    }
  `);
}
